import { AbilityBuilder } from '@casl/ability';
import { createPrismaAbility } from '@casl/prisma';
import { filterPermissionsByModules, getModulesFromCompany } from './moduleSubjects';
import {
  allActions,
  allSubjects,
  type AppAbility,
  type Rules,
  type Actions,
  type AppSubjects
} from '~/types/permissions';
import type AuthUser from '~/types/authUser';
import { getUniversalPermissionsFromRole } from './defineUniversalAbility';

export type RoleWithRules = {
  name: string;
  rules: Rules[];
};
export type RolesWithRulesArray = RoleWithRules[];

function getPermissionsFromRole(user: AuthUser): Rules[] {
  const rolesWithRules: RolesWithRulesArray = [
    {
      name: 'superadmin',
      rules: [['manage', 'All']]
    },
    {
      name: 'admin',
      rules: [
        [
          'manage',
          'User',
          {
            OR: [
              {
                roles: {
                  some: {
                    companyId: user.companyId
                  }
                }
              },
              { id: user.id }
            ]
          }
        ],
        ['view', 'AuditLog'],
        ['read', 'AuditLog'],
        ['view', 'Company', { id: user.companyId }],
        ['read', 'Company', { id: user.companyId }],
        ['update', 'Company', { id: user.companyId }],
        ['read', 'Plan'],
        ['read', 'Module'],
        ['manage', 'PlanSubscription', { companyId: user.companyId }],
        ['read', 'PeriodicalFee'],
        [
          'manage',
          'uiRole',
          {
            OR: [
              {
                roles: {
                  some: {
                    companyId: user.companyId
                  }
                }
              },
              { id: user.id }
            ]
          }
        ],
        [
          'view',
          'uiRole',
          {
            OR: [
              {
                roles: {
                  some: {
                    companyId: user.companyId
                  }
                }
              },
              { id: user.id }
            ]
          }
        ],
        ['read', 'Tutorial'],
        ['manage', 'Periodicity'],
        ['view', 'Periodicity'],
        ['manage', 'Local', { companyId: user.companyId }],
        ['view', 'Local'],
        ['manage', 'Equipment', { local: { companyId: user.companyId } }],
        ['view', 'Equipment'],
        ['read', 'StandardEquipment'],
        ['view', 'Supplier'],
        ['create', 'Supplier', { companyId: user.companyId }],
        ['read', 'Supplier', { OR: [{ companyId: user.companyId }, { companyId: null }] }],
        ['update', 'Supplier', { companyId: user.companyId }],
        ['delete', 'Supplier', { companyId: user.companyId }],
        ['manage', 'Call', { companyId: user.companyId }],
        ['view', 'Maintenance'],
        ['manage', 'Maintenance', { companyId: user.companyId }],
        ['read', 'MaintenancePlanItem'],
        ['update', 'MaintenancePlanItem'],
        ['view', 'MaintenancePlanItem'],
        ['manage', 'CompanyPlanSelection', { companyId: user.companyId }],
        ['view', 'Start'],
        ['view', 'WAMessage'],
        ['manage', 'Warehouse', { companyId: user.companyId }],
        ['view', 'Stock'],
        ['view', 'StockIn'],
        ['view', 'StockOut'],
        ['view', 'StockRecord'],
        ['manage', 'StockItem', { companyId: user.companyId }],
        ['view', 'StockItem'],
        ['manage', 'StockMovimentation', { companyId: user.companyId }],
        ['view', 'StockMovimentation', { companyId: user.companyId }],
        ['manage', 'WasteType', { companyId: user.companyId }],
        ['read', 'WasteType', { OR: [{ companyId: user.companyId }, { companyId: null }] }],
        ['view', 'WasteType'],
        ...(user.companyId
          ? ([
              ['view', 'FuelGaseClass'],
              ['read', 'FuelGaseClass', { OR: [{ companyId: user.companyId }, { companyId: null }] }],
              ['create', 'FuelGaseClass'],
              ['update', 'FuelGaseClass', { companyId: user.companyId }],
              ['delete', 'FuelGaseClass', { companyId: user.companyId }]
            ] as Rules[])
          : []),
        ['manage', 'WasteContainer', { companyId: user.companyId }],
        ['view', 'WasteContainer'],
        ['manage', 'FuelGaseContainer', { local: { companyId: user.companyId } }],
        ['view', 'FuelGaseContainer'],
        ['manage', 'FuelGaseMovimentation', { container: { local: { companyId: user.companyId } } }],
        ['view', 'FuelGaseMovimentation'],
        ['manage', 'FuelGaseMedition', { container: { local: { companyId: user.companyId } } }],
        ['view', 'FuelGaseMedition'],
        ['manage', 'WasteCollection', { container: { local: { companyId: user.companyId } } }],
        ['manage', 'FuelGaseInspection', { container: { local: { companyId: user.companyId } } }],
        ['view', 'FuelGaseInspection'],
        ['view', 'WasteCollection'],
        ['manage', 'WasteGeneration', { container: { local: { companyId: user.companyId } } }],
        ['view', 'WasteGeneration'],
        ['manage', 'WasteInspection', { companyId: user.companyId }],
        ['view', 'WasteInspection'],
        ['manage', 'Reading'],
        ['view', 'multiBuildingComplexes'],
        ['read', 'multiBuildingComplexes']
      ]
    },
    {
      name: 'supervisor',
      rules: [
        [
          'manage',
          'User',
          {
            OR: [
              {
                roles: {
                  some: {
                    companyId: user.companyId,
                    roleName: 'collaborator'
                  }
                }
              },
              { id: user.id }
            ]
          }
        ],
        ['view', 'Company', { id: user.companyId }],
        ['read', 'Company', { id: user.companyId }],
        ['read', 'Plan'],
        ['read', 'Module'],
        ['read', 'PlanSubscription', { companyId: user.companyId }],
        ['PeriodicalFee', 'read'],
        ['create', 'StockItem', { companyId: user.companyId }],
        ['update', 'StockItem', { companyId: user.companyId }],
        ['read', 'StockItem', { companyId: user.companyId }],
        ['view', 'StockItem'],
        ['create', 'StockMovimentation', { companyId: user.companyId }],
        ['update', 'StockMovimentation', { companyId: user.companyId }],
        ['read', 'StockMovimentation', { companyId: user.companyId }],
        ['view', 'StockMovimentation', { companyId: user.companyId }],
        [
          'manage',
          'uiRole',
          {
            OR: [
              {
                roles: {
                  some: {
                    companyId: user.companyId,
                    roleName: 'collaborator'
                  }
                }
              },
              { id: user.id }
            ]
          }
        ],
        [
          'view',
          'uiRole',
          {
            OR: [
              {
                roles: {
                  some: {
                    companyId: user.companyId
                  }
                }
              },
              { id: user.id }
            ]
          }
        ],
        ['read', 'Tutorial'],
        ['read', 'Periodicity'],
        ['view', 'Periodicity'],
        ['update', 'Periodicity'],
        ['create', 'Periodicity'],
        ['update', 'Local', { companyId: user.companyId }],
        ['view', 'Local', { companyId: user.companyId }],
        ['read', 'Local', { companyId: user.companyId }],
        ['create', 'Local', { companyId: user.companyId }],
        ['create', 'Equipment', { local: { companyId: user.companyId } }],
        ['view', 'Equipment'],
        ['read', 'Equipment', { local: { companyId: user.companyId } }],
        ['read', 'StandardEquipment'],
        ['view', 'StandardEquipment'],
        ['create', 'StandardEquipment'],
        ['update', 'StandardEquipment'],
        ['create', 'Supplier', { companyId: user.companyId }],
        ['update', 'Supplier', { companyId: user.companyId }],
        ['view', 'Supplier'],
        [
          'read',
          'Supplier',
          {
            OR: [{ companyId: user.companyId }, { companyId: null }]
          }
        ],
        ['view', 'Call'],
        ['read', 'Call', { companyId: user.companyId }],
        ['create', 'Call', { companyId: user.companyId }],
        ['update', 'Call', { companyId: user.companyId }],
        ['read', 'Maintenance', { companyId: user.companyId }],
        ['update', 'Maintenance', { companyId: user.companyId }],
        ['create', 'Maintenance', { companyId: user.companyId }],
        ['view', 'Maintenance'],
        ['read', 'MaintenancePlanItem'],
        ['view', 'MaintenancePlanItem'],
        ['read', 'CompanyPlanSelection', { companyId: user.companyId }],
        ['update', 'CompanyPlanSelection', { companyId: user.companyId }],
        ['read', 'StockItem', { companyId: user.companyId }],
        ['view', 'StockItem'],
        ['view', 'Start'],
        ['view', 'Start'],
        ['view', 'Warehouse'],
        ['read', 'Warehouse', { companyId: user.companyId }],
        ['view', 'Stock'],
        ['manage', 'WasteType', { companyId: user.companyId }],
        ['view', 'WasteType'],
        ['manage', 'WasteContainer', { companyId: user.companyId }],
        ['view', 'WasteContainer'],
        ['manage', 'FuelGaseContainer', { local: { companyId: user.companyId } }],
        ['view', 'FuelGaseContainer'],
        ['manage', 'FuelGaseMovimentation', { container: { local: { companyId: user.companyId } } }],
        ['view', 'FuelGaseMovimentation'],
        ['manage', 'FuelGaseMedition', { container: { local: { companyId: user.companyId } } }],
        ['view', 'FuelGaseMedition'],
        ['read', 'WasteCollection'],
        ['manage', 'FuelGaseInspection', { container: { local: { companyId: user.companyId } } }],
        ['view', 'FuelGaseInspection'],
        ['view', 'WasteCollection'],
        ['view', 'WasteGeneration'],
        ['view', 'WasteInspection'],
        ['manage', 'Reading'],
        ['view', 'multiBuildingComplexes'],
        ['read', 'multiBuildingComplexes']
      ]
    },
    {
      name: 'collaborator',
      rules: [
        ['view', 'User', { id: user.id }],
        ['read', 'User', { id: user.id }],
        ['update', 'User', { id: user.id }],
        ['view', 'Company', { id: user.companyId }],
        ['read', 'Company', { id: user.companyId }],
        ['read', 'Plan'],
        ['read', 'PlanSubscription', { companyId: user.companyId }],
        ['read', 'Module'],
        ['read', 'PeriodicalFee'],
        ['update', 'UIRole', { id: user.id }],
        ['read', 'UIRole'],
        ['read', 'Tutorial'],
        ['read', 'Periodicity'],
        ['read', 'Local', { companyId: user.companyId }],
        ['read', 'Equipment', { local: { companyId: user.companyId } }],
        ['read', 'Supplier', { OR: [{ companyId: user.companyId }, { companyId: null }] }],
        ['create', 'Supplier', { companyId: user.companyId }],
        [
          'read',
          'Call',
          {
            show2colaborator: true,
            maintenance: {
              collaboratorIds: { has: user.id }
            }
          }
        ],
        ['update', 'Maintenance', { collaboratorIds: { has: user.id } }],
        ['read', 'Maintenance', { collaboratorIds: { has: user.id } }],
        ['view', 'Maintenance'],
        ['view', 'Start'],
        ['read', 'Warehouse', { companyId: user.companyId }],
        ['read', 'Stock'],
        ['read', 'StockItem', { companyId: user.companyId }],
        ['create', 'StockOut', { companyId: user.companyId }],
        ['update', 'StockOut', { companyId: user.companyId }],
        ['delete', 'StockOut', { companyId: user.companyId }],
        ['read', 'StockOut', { companyId: user.companyId }],
        ['view', 'WasteType'],
        ['read', 'WasteType'],
        ['view', 'WasteContainer'],
        ['read', 'WasteContainer'],
        ['view', 'FuelGaseContainer'],
        ['read', 'FuelGaseContainer', { local: { companyId: user.companyId } }],
        ['view', 'FuelGaseMovimentation'],
        ['read', 'FuelGaseMovimentation', { container: { local: { companyId: user.companyId } } }],
        ['view', 'FuelGaseMedition'],
        ['read', 'FuelGaseMedition', { container: { local: { companyId: user.companyId } } }],
        ['read', 'WasteCollection'],
        ['view', 'FuelGaseInspection'],
        ['read', 'FuelGaseInspection', { container: { local: { companyId: user.companyId } } }],
        ['view', 'WasteCollection'],
        ['view', 'WasteGeneration'],
        ['view', 'WasteInspection'],
        ['view', 'Reading'],
        ['read', 'Reading'],
        ['create', 'Reading'],
        ['update', 'Reading'],
        ['view', 'multiBuildingComplexes'],
        ['read', 'multiBuildingComplexes']
      ]
    },
    {
      name: 'auditor',
      rules: [
        [
          'view',
          'User',
          {
            OR: [
              {
                roles: {
                  some: {
                    companyId: user.companyId
                  }
                }
              },
              { id: user.id }
            ]
          }
        ],
        [
          'read',
          'User',
          {
            OR: [
              {
                roles: {
                  some: {
                    companyId: user.companyId
                  }
                }
              },
              { id: user.id }
            ]
          }
        ],
        ['update', 'User', { id: user.id }],
        ['read', 'Company', { id: user.companyId }],
        ['view', 'Company', { id: user.companyId }],
        ['read', 'Plan'],
        ['read', 'Module'],
        ['read', 'PlanSubscription', { companyId: user.companyId }],
        ['read', 'uiRole', { id: user.id }],
        ['update', 'uiRole', { id: user.id }],
        ['read', 'Tutorial'],
        ['view', 'Periodicity'],
        ['read', 'Periodicity'],
        ['read', 'Local', { companyId: user.companyId }],
        ['view', 'Local', { companyId: user.companyId }],
        ['read', 'Equipment', { local: { companyId: user.companyId } }],
        ['view', 'Equipment', { local: { companyId: user.companyId } }],
        ['read', 'StandardEquipment'],
        ['read', 'StandardEquipment'],
        ['view', 'Supplier', { OR: [{ companyId: user.companyId }, { companyId: null }] }],
        ['read', 'Supplier', { OR: [{ companyId: user.companyId }, { companyId: null }] }],
        ['read', 'Call', { companyId: user.companyId }],
        ['view', 'Call', { companyId: user.companyId }],
        ['view', 'Maintenance', { companyId: user.companyId }],
        ['read', 'Maintenance', { companyId: user.companyId }],
        ['read', 'MaintenancePlanItem'],
        ['view', 'MaintenancePlanItem'],
        ['read', 'CompanyPlanSelection', { companyId: user.companyId }],
        ['update', 'UIRole'],
        ['view', 'Start'],
        ['read', 'StockItem', { companyId: user.companyId }],
        ['view', 'StockItem', { companyId: user.companyId }],
        ['view', 'Warehouse', { companyId: user.companyId }],
        ['read', 'Warehouse', { companyId: user.companyId }],
        ['view', 'Stock'],
        ['read', 'StockMovimentation', { companyId: user.companyId }],
        ['view', 'StockMovimentation', { companyId: user.companyId }],
        ['view', 'WasteType'],
        ['read', 'WasteType'],
        ['view', 'WasteContainer'],
        ['read', 'WasteContainer'],
        ['view', 'FuelGaseContainer'],
        ['read', 'FuelGaseContainer', { local: { companyId: user.companyId } }],
        ['view', 'FuelGaseMovimentation'],
        ['read', 'FuelGaseMovimentation', { container: { local: { companyId: user.companyId } } }],
        ['view', 'FuelGaseMedition'],
        ['read', 'FuelGaseMedition', { container: { local: { companyId: user.companyId } } }],
        ['read', 'WasteCollection'],
        ['view', 'FuelGaseInspection'],
        ['read', 'FuelGaseInspection', { container: { local: { companyId: user.companyId } } }],
        ['view', 'WasteCollection'],
        ['view', 'WasteGeneration'],
        ['view', 'WasteInspection'],
        ['view', 'Reading'],
        ['read', 'Reading'],
        ['view', 'multiBuildingComplexes'],
        ['read', 'multiBuildingComplexes']
      ]
    },
    {
      name: 'guest',
      rules: [
        ['create', 'User'],
        ['read', 'Tutorial'],
        ['read', 'Plan']
      ]
    }
  ];

  return rolesWithRules.find((role) => user.roleName === role.name)?.rules || [];
}

export function defineUserAbility(authUser?: AuthUser | null) {
  const user =
    authUser ||
    ({
      id: 0,
      name: 'Guest',
      roles: [{ roleName: 'guest' }]
    } as unknown as AuthUser);

  if (!user.roleName) {
    user.roleName = user.roles?.[0]?.roleName || 'guest';
  }

  const { can, build } = new AbilityBuilder<AppAbility>(createPrismaAbility);

  if (user.companyStatus === 2) {
    const universalPermissions = getUniversalPermissionsFromRole(user);

    universalPermissions.forEach(([action, subject, conditions]) => {
      const subjectsToProcess = subject === 'All' ? allSubjects.filter((s) => s !== 'All') : [subject];
      const actionsToProcess = action === 'manage' ? allActions.filter((a) => a !== 'manage') : [action];
      actionsToProcess.forEach((act: Actions) => {
        subjectsToProcess.forEach((subj: AppSubjects) => {
          if (conditions && Object.keys(conditions).length > 0) {
            can(act, subj, conditions as never);
          } else {
            can(act, subj);
          }
        });
      });
    });

    return build();
  }

  const rolesPermissions = getPermissionsFromRole(user);

  const companyModules = getModulesFromCompany(user);

  const filteredRolePermissions = filterPermissionsByModules(rolesPermissions, companyModules);

  const allPermissions = [...filteredRolePermissions];

  allPermissions.forEach(([action, subject, conditions]) => {
    const subjectsToProcess = subject === 'All' ? allSubjects.filter((s) => s !== 'All') : [subject];
    const actionsToProcess = action === 'manage' ? allActions.filter((a) => a !== 'manage') : [action];
    actionsToProcess.forEach((act: Actions) => {
      subjectsToProcess.forEach((subj: AppSubjects) => {
        if (conditions && Object.keys(conditions).length > 0) {
          can(act, subj, conditions as never);
        } else {
          can(act, subj);
        }
      });
    });
  });
  return build();
}
